import { useEffect } from "react";

import { ACTION } from "./editorModel";
import { fetchPreviewMatch } from "./editorSubmit";
import { MATCH_STATUS, MATCH_TIMEOUT_MS, matchRequest } from "./matchModel";

const MATCH_DEGRADED = "degraded"; // the server's status when it could not match (timeout, embedder or graph unavailable)

/**
 * Asks the server for the preview match once per Review screen: when the preview's match is `loading` (Review pressed, or Retry).
 * Never runs while typing. The request is abandoned after MATCH_TIMEOUT_MS and cancelled when the screen goes away; both a
 * failure and a timeout leave every row as "new" with a Retry chip. Without a sessionId nothing is asked.
 */
export default function usePreviewMatch({ preview, category, sessionId, dispatch }) {
  const status = preview ? preview.match.status : null;
  const key = preview ? preview.match.key : null;
  const draft = preview ? preview.draft : null;

  useEffect(() => {
    if (!sessionId || status !== MATCH_STATUS.LOADING) return undefined;
    const controller = new AbortController();
    let stale = false; // the screen went away or a newer request replaced this one: ignore the answer
    const timer = setTimeout(() => controller.abort(), MATCH_TIMEOUT_MS);
    fetchPreviewMatch(sessionId, matchRequest(category, draft), controller.signal)
      .then((data) => {
        if (stale) return;
        // a degraded answer (the server ran out of time or could not match) carries no matches: say so and offer Retry
        dispatch(data && data.status === MATCH_DEGRADED ? { type: ACTION.MATCH_FAILED, key } : { type: ACTION.MATCH_RESOLVED, key, data });
      })
      .catch(() => { if (!stale) dispatch({ type: ACTION.MATCH_FAILED, key }); })
      .finally(() => clearTimeout(timer));
    return () => { stale = true; clearTimeout(timer); controller.abort(); };
  }, [sessionId, status, key, category, draft, dispatch]);
}
